import React, { useEffect, useState } from "react";
import {
  Save,
  Key,
  Users,
  Bell,
  Shield,
  Eye,
  EyeOff,
  Plus,
  Trash2,
  Edit,
  AlertTriangle,
  CheckCircle,
  Mail,
  Twitter,
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Switch } from "../ui/switch";
import { Badge } from "../ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { toast } from "sonner";
import {
  useChangeSubAdminStatusApiCallMutation,
  useCreateSubAdminApiCallMutation,
  useDeleteSubAdminApiCallMutation,
  useGetProfileApiCallQuery,
  useGetSubAdminApiCallQuery,
  useUpdateProfileApiCallMutation,
} from "../../services/api/auth/auth.api";
import {
  ApiError,
  UserData,
  UserType,
} from "../../services/api/auth/auth.type";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import TwitterLogin from "../twitter/Twitter";
import InstagramLogin from "../instagram/Instagram";
import { useViewSocialsCredentialsApiCallQuery } from "../../services/api/campaigns/campaigns.api";

interface AdminUser {
  id: string;
  name: string;
  email: string;
  role: "super_admin" | "campaign_manager";
  status: "active" | "invited" | "suspended";
  lastLogin: string;
  createdDate: string;
}

const mockAdminUsers: AdminUser[] = [
  {
    id: "1",
    name: "Admin User",
    email: "admin@airdrop.com",
    role: "super_admin",
    status: "active",
    lastLogin: "2024-01-28T10:30:00Z",
    createdDate: "2024-01-01T00:00:00Z",
  },
  {
    id: "2",
    name: "Campaign Manager",
    email: "manager@airdrop.com",
    role: "campaign_manager",
    status: "active",
    lastLogin: "2024-01-27T15:45:00Z",
    createdDate: "2024-01-15T00:00:00Z",
  },
  {
    id: "3",
    name: "John Doe",
    email: "john@airdrop.com",
    role: "campaign_manager",
    status: "invited",
    lastLogin: "Never",
    createdDate: "2024-01-25T00:00:00Z",
  },
];

const Permissions = [
  "Campaigns",
  "Users",
  "Transactions",
  "Wallets",
  "Settings",
  "Referrals Code",
  "Referrals Code Users",
];

const SystemSettings: React.FC = () => {
  const [apiKeys, setApiKeys] = useState({
    twitterApiKey: "",
    twitterApiSecret: "",
    twitterAccessToken: "",
    twitterAccessTokenSecret: "",
    telegramBotToken: "",
  });

  const [showApiKeys, setShowApiKeys] = useState({
    twitterApiKey: false,
    twitterApiSecret: false,
    twitterAccessToken: false,
    twitterAccessTokenSecret: false,
    telegramBotToken: false,
  });

  const [notifications, setNotifications] = useState<UserType>({
    lowBalanceAlert: false,
    newCampaignAlert: false,
  });

  const [socailCredential, setSocailCredential] = useState({
    twitter: false,
    instagram: false,
  });

  const [adminUsers, setAdminUsers] = useState<AdminUser[]>(mockAdminUsers);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    name: "",
    email: "",
    role: "campaign_manager" as "super_admin" | "campaign_manager",
  });
  const [permissions, setPermission] = useState<string[]>([]);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [reason, setReason] = useState("");
  const [suspendReason, setSuspendReason] = useState<string>("");
  const [suspendTargetId, setSuspendTargetId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);

  const { data: subAdminData } = useGetSubAdminApiCallQuery({
    page: page,
    limit: 10,
  });
  const { data: userProfile } = useGetProfileApiCallQuery();
  const { data: socialTwitter } = useViewSocialsCredentialsApiCallQuery({
    platform: "Twitter",
  });

  const [createSubAdmin] = useCreateSubAdminApiCallMutation();
  const [deleteSubAdmin] = useDeleteSubAdminApiCallMutation();

  const [updateProfile] = useUpdateProfileApiCallMutation();
  const [changeSubAdminStatus] = useChangeSubAdminStatusApiCallMutation();

  const handleSetPermissions = (perm: string) => {
    setPermission((prev) =>
      prev.includes(perm) ? prev.filter((p) => p !== perm) : [...prev, perm]
    );

    // setPermission((prev) =>
    //   prev.includes(perm:) ? prev.filter((p) => p !== perm) : [...prev, perm]
    // );
  };

  const confirmDelete = async () => {
    setIsDeleteLoading(true);
    try {
      const data = await deleteSubAdmin({
        userId: deleteTargetId as string,
      }).unwrap();

      if (data.responseCode === 200) {
        toast.success(data.responseMessage);
        setDeleteTargetId(null);
      }
    } catch (error) {
      const fetchError = error as FetchBaseQueryError;
      const errorData = fetchError.data as ApiError;
      toast.error(errorData?.responseMessage || "Error deleting campaign");
    } finally {
      setIsDeleteLoading(false);
      setDeleteTargetId(null);
    }
  };

  const confirmSuspend = async () => {
    try {
      const data = await changeSubAdminStatus({
        userId: suspendTargetId as string,
        reason: suspendReason,
      }).unwrap();
      if (data.responseCode === 200) {
        toast.success(data.responseMessage);
        setSuspendTargetId(null);
      }
    } catch (error) {
      const fetchError = error as FetchBaseQueryError;
      const errorData = fetchError.data as ApiError;
      toast.error(errorData?.responseMessage || "Error deleting campaign");
    }
  };

  const toggleApiKeyVisibility = (key: keyof typeof showApiKeys) => {
    setShowApiKeys((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleApiKeyChange = (key: keyof typeof apiKeys, value: string) => {
    setApiKeys((prev) => ({ ...prev, [key]: value }));
  };

  const handleSaveApiKeys = () => {
    // Simulate API call
    toast.success("API keys saved successfully!");
  };

  const handleNotificationChange = (
    key: keyof typeof notifications,
    value: boolean | string
  ) => {
    setNotifications((prev) => ({ ...prev, [key]: value }));
  };

  const handleSaveNotifications = async () => {
    // Simulate API call
    // toast.success("Notification settings saved successfully!");
    try {
      const response = await updateProfile({
        lowBalanceAlert: notifications?.lowBalanceAlert,
        newCampaignAlert: notifications?.newCampaignAlert,
      }).unwrap();
      if (response.responseCode === 200) {
        toast.success(response?.responseMessage);
      }
    } catch (error) {
      const fetchError = error as FetchBaseQueryError;
      const errorData = fetchError.data as ApiError;
      toast.error(errorData?.responseMessage || "Login failed");
    }
  };

  const handleInviteAdmin = async () => {
    if (!inviteForm.name || !inviteForm.email) {
      toast.error("Please fill in all fields");
      return;
    }

    const newAdmin: AdminUser = {
      id: Date.now().toString(),
      name: inviteForm.name,
      email: inviteForm.email,
      role: inviteForm.role,
      status: "invited",
      lastLogin: "Never",
      createdDate: new Date().toISOString(),
    };

    try {
      setLoading(true);
      const response = await createSubAdmin({
        firstName: inviteForm.name,
        email: inviteForm.email,
        permissions: permissions,
      }).unwrap();

      if (response.responseCode === 200) {
        toast.success(response?.responseMessage);
        setShowInviteDialog(false);
      } else {
        toast.error(response?.responseMessage);
      }
    } catch (error) {
      const fetchError = error as FetchBaseQueryError;
      const errorData = fetchError.data as ApiError;
      toast.error(errorData?.responseMessage || "Login failed");
    } finally {
      setLoading(false);
    }

    // setAdminUsers((prev) => [...prev, newAdmin]);
    // setInviteForm({ name: "", email: "", role: "campaign_manager" });
    // setShowInviteDialog(false);
  };

  const handleSuspendAdmin = async (adminId: string, status: string) => {
    if (status === "SUSPENDED") {
      setSuspendTargetId(adminId);
    } else {
      try {
        const data = await changeSubAdminStatus({
          userId: adminId as string,
        }).unwrap();
        if (data.responseCode === 200) {
          toast.success(data.responseMessage);
          setSuspendTargetId(null);
        }
      } catch (error) {
        const fetchError = error as FetchBaseQueryError;
        const errorData = fetchError.data as ApiError;
        toast.error(errorData?.responseMessage || "Error deleting campaign");
      }
    }

    // toast.success("Admin status updated successfully!");
  };

  const handleDeleteAdmin = (adminId: string) => {
    setAdminUsers((prev) => prev.filter((admin) => admin.id !== adminId));
    toast.success("Admin removed successfully!");
  };

  const getRoleLabel = (role: string) => {
    return role === "super_admin" ? "Super Admin" : "Campaign Manager";
  };

  const getStatusBadge = (status: UserData["status"]) => {
    const colors = {
      ACTIVE: "bg-green-100 text-green-800",
      INACTIVE: "bg-yellow-100 text-yellow-800",
      SUSPENDED: "bg-red-100 text-red-800",
    };

    return (
      <Badge className={colors[status]}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  useEffect(() => {
    if (userProfile && userProfile?.result) {
      setNotifications((prev) => ({
        ...prev,
        lowBalanceAlert: userProfile?.result?.lowBalanceAlert || false,
        newCampaignAlert: userProfile?.result?.newCampaignAlert || false,
      }));
    }
  }, [userProfile]);

  useEffect(() => {
    if (socialTwitter) {
      setSocailCredential((prev) => ({
        ...prev,
        twitter: true,
      }));
    }
  }, [socialTwitter]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">
          System Settings
        </h1>
        <p className="text-gray-600 mt-1">
          Configure platform-wide settings and integrations
        </p>
      </div>

      {/* Settings Tabs */}
      <Tabs defaultValue="admins" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          {/* <TabsTrigger value="apis" className="flex items-center space-x-2">
            <Key className="h-4 w-4" />
            <span>API Keys</span>
          </TabsTrigger> */}
          <TabsTrigger value="admins" className="flex items-center space-x-2">
            <Users className="h-4 w-4" />
            <span>Subadmin Users</span>
          </TabsTrigger>
          <TabsTrigger
            value="notifications"
            className="flex items-center space-x-2"
          >
            <Bell className="h-4 w-4" />
            <span>Notifications</span>
          </TabsTrigger>
        </TabsList>

        {/* <TabsContent value="apis" className="space-y-6">
          Twitter API Settings
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <div className="w-6 h-6 bg-blue-500 rounded flex items-center justify-center">
                  <span className="text-white text-xs font-bold">T</span>
                </div>
                <span>Twitter API Configuration</span>
              </CardTitle>
              <CardDescription>
                Configure Twitter API credentials for automated verification of
                Twitter tasks
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-4">
              <TwitterLogin isConnected={socailCredential?.twitter} />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="twitterApiKey">API Key</Label>
                  <div className="relative">
                    <Input
                      id="twitterApiKey"
                      type={showApiKeys.twitterApiKey ? "text" : "password"}
                      placeholder="Enter Twitter API Key"
                      value={apiKeys.twitterApiKey}
                      onChange={(e) =>
                        handleApiKeyChange("twitterApiKey", e.target.value)
                      }
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => toggleApiKeyVisibility("twitterApiKey")}
                    >
                      {showApiKeys.twitterApiKey ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="twitterApiSecret">API Secret</Label>
                  <div className="relative">
                    <Input
                      id="twitterApiSecret"
                      type={showApiKeys.twitterApiSecret ? "text" : "password"}
                      placeholder="Enter Twitter API Secret"
                      value={apiKeys.twitterApiSecret}
                      onChange={(e) =>
                        handleApiKeyChange("twitterApiSecret", e.target.value)
                      }
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => toggleApiKeyVisibility("twitterApiSecret")}
                    >
                      {showApiKeys.twitterApiSecret ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="twitterAccessToken">Access Token</Label>
                  <div className="relative">
                    <Input
                      id="twitterAccessToken"
                      type={
                        showApiKeys.twitterAccessToken ? "text" : "password"
                      }
                      placeholder="Enter Access Token"
                      value={apiKeys.twitterAccessToken}
                      onChange={(e) =>
                        handleApiKeyChange("twitterAccessToken", e.target.value)
                      }
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() =>
                        toggleApiKeyVisibility("twitterAccessToken")
                      }
                    >
                      {showApiKeys.twitterAccessToken ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="twitterAccessTokenSecret">
                    Access Token Secret
                  </Label>
                  <div className="relative">
                    <Input
                      id="twitterAccessTokenSecret"
                      type={
                        showApiKeys.twitterAccessTokenSecret
                          ? "text"
                          : "password"
                      }
                      placeholder="Enter Access Token Secret"
                      value={apiKeys.twitterAccessTokenSecret}
                      onChange={(e) =>
                        handleApiKeyChange(
                          "twitterAccessTokenSecret",
                          e.target.value
                        )
                      }
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() =>
                        toggleApiKeyVisibility("twitterAccessTokenSecret")
                      }
                    >
                      {showApiKeys.twitterAccessTokenSecret ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button
                  onClick={handleSaveApiKeys}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save Twitter Settings
                </Button>
              </div>
            </CardContent>
          </Card>

          Telegram API Settings
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <div className="w-6 h-6 bg-blue-400 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">IG</span>
                </div>
                <span>Instagram Configuration</span>
              </CardTitle>
              <CardDescription>
                Configure Instagram for automated verification of Instagram
                tasks
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-4">
              <InstagramLogin />
            </CardContent>
          </Card>

          Security Warning
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-4">
              <div className="flex items-start space-x-3">
                <Shield className="h-5 w-5 text-amber-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-amber-900 mb-1">
                    Security Notice
                  </h4>
                  <p className="text-sm text-amber-800">
                    API keys are encrypted and stored securely. Only enter
                    production API keys in a production environment. Never share
                    these credentials or commit them to version control.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent> */}

        <TabsContent value="admins" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Administrator Accounts</CardTitle>
                  <CardDescription>
                    Manage admin users and their access levels
                  </CardDescription>
                </div>

                <Dialog
                  open={showInviteDialog}
                  onOpenChange={setShowInviteDialog}
                >
                  <DialogTrigger asChild>
                    <Button className="bg-blue-600 hover:bg-blue-700">
                      <Plus className="h-4 w-4 mr-2" />
                      Invite Admin
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Invite New Administrator</DialogTitle>
                      <DialogDescription>
                        Send an invitation to a new admin user
                      </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="adminName">Full Name</Label>
                        <Input
                          id="adminName"
                          placeholder="Enter full name"
                          value={inviteForm.name}
                          onChange={(e) =>
                            setInviteForm((prev) => ({
                              ...prev,
                              name: e.target.value,
                            }))
                          }
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="adminEmail">Email Address</Label>
                        <Input
                          id="adminEmail"
                          type="email"
                          placeholder="Enter email address"
                          value={inviteForm.email}
                          onChange={(e) =>
                            setInviteForm((prev) => ({
                              ...prev,
                              email: e.target.value,
                            }))
                          }
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="adminRole">Permissions</Label>

                        <div className="flex flex-wrap gap-4">
                          {Permissions?.map((permission) => (
                            <div key={permission}>
                              <label className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  name="role"
                                  // value={permission}
                                  checked={permissions.includes(permission)}
                                  onChange={() =>
                                    handleSetPermissions(permission)
                                  }
                                  // onChange={() =>
                                  //   setInviteForm((prev) => ({
                                  //     ...prev,
                                  //     role: permission,
                                  //   }))
                                  // }
                                />
                                <span>{permission}</span>
                              </label>
                            </div>
                          ))}
                        </div>
                        {/* <Select
                          value={inviteForm.role}
                          onValueChange={(
                            value: "super_admin" | "campaign_manager"
                          ) =>
                            setInviteForm((prev) => ({ ...prev, role: value }))
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="campaign_manager">
                              Campaign Manager
                            </SelectItem>
                            <SelectItem value="super_admin">
                              Super Admin
                            </SelectItem>
                          </SelectContent>
                        </Select> */}
                        <p className="text-sm text-gray-500">
                          Campaign Managers can create and manage campaigns.
                          Super Admins have full system access.
                        </p>
                      </div>
                    </div>

                    <DialogFooter>
                      <Button
                        variant="outline"
                        onClick={() => setShowInviteDialog(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        disabled={loading}
                        onClick={handleInviteAdmin}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        <Mail className="h-4 w-4 mr-2" />

                        {loading ? "Inviting..." : "Send Invitation"}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>

            <CardContent>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      {/* <TableHead>Role</TableHead> */}
                      <TableHead>Status</TableHead>
                      {/* <TableHead>Last Login</TableHead> */}
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {subAdminData?.result?.docs &&
                      subAdminData?.result?.docs?.length > 0 &&
                      subAdminData?.result?.docs.map((admin) => (
                        <TableRow key={admin._id}>
                          <TableCell className="font-medium">
                            {admin.firstName}
                          </TableCell>
                          <TableCell>{admin.email}</TableCell>
                          {/* <TableCell>
                          <Badge variant="outline">
                            {getRoleLabel(admin.role)}
                          </Badge>
                        </TableCell> */}
                          <TableCell>{getStatusBadge(admin.status)}</TableCell>
                          {/* <TableCell>
                          {admin.lastLogin === "Never" ? (
                            <span className="text-gray-500">Never</span>
                          ) : (
                            new Date(admin.lastLogin).toLocaleDateString()
                          )}
                        </TableCell> */}
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end space-x-2">
                              {admin.status === "ACTIVE" ? (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() =>
                                    handleSuspendAdmin(admin._id, "SUSPENDED")
                                  }
                                >
                                  Suspend
                                </Button>
                              ) : (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() =>
                                    handleSuspendAdmin(admin._id, "ACTIVE")
                                  }
                                  className="text-green-600 border-green-200"
                                >
                                  Reactivate
                                </Button>
                              )}

                              {admin._id !== "1" && ( // Don't allow deleting the main admin
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setDeleteTargetId(admin._id)}
                                  className="text-red-600 border-red-200"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>

                {subAdminData?.result && subAdminData?.result?.pages > 1 && (
                  <div className="flex justify-end items-center mt-4 gap-2">
                    <Button
                      variant="outline"
                      disabled={page === 1}
                      onClick={() => setPage((prev) => prev - 1)}
                    >
                      Previous
                    </Button>
                    <span>
                      Page {page} of {subAdminData.result?.pages}
                    </span>
                    <Button
                      variant="outline"
                      disabled={page === subAdminData.result.pages}
                      onClick={() => setPage((prev) => prev + 1)}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </div>

              {deleteTargetId && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
                  <div className="bg-white dark:bg-gray-900 p-6 rounded-xl shadow-xl max-w-sm w-full">
                    <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                      Confirm Delete
                    </h2>
                    <p className="mb-6 text-gray-600 dark:text-gray-300">
                      Are you sure you want to delete?
                    </p>
                    {/* Reason Input */}
                    <input
                      type="text"
                      placeholder="Enter reason for deletion..."
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      className="w-full mb-6 p-2 border border-gray-300 rounded-md dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                    />
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="secondary"
                        onClick={() => setDeleteTargetId(null)}
                      >
                        Cancel
                      </Button>
                      <Button
                        className="bg-red-600 hover:bg-red-700 text-white flex items-center gap-2"
                        onClick={confirmDelete}
                        disabled={isDeleteLoading || !reason}
                      >
                        Yes, Delete{" "}
                        {isDeleteLoading && (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {suspendTargetId && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
                  <div className="bg-white dark:bg-gray-900 p-6 rounded-xl shadow-xl max-w-sm w-full">
                    <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                      Confirm Suspended
                    </h2>
                    <p className="mb-6 text-gray-600 dark:text-gray-300">
                      Are you sure you want to suspend?
                    </p>
                    {/* Reason Input */}
                    <input
                      type="text"
                      placeholder="Enter reason for Suspended..."
                      value={suspendReason}
                      onChange={(e) => setSuspendReason(e.target.value)}
                      className="w-full mb-6 p-2 border border-gray-300 rounded-md dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                    />
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="secondary"
                        onClick={() => setSuspendTargetId(null)}
                      >
                        Cancel
                      </Button>
                      <Button
                        className="bg-red-600 hover:bg-red-700 text-white flex items-center gap-2"
                        onClick={confirmSuspend}
                        disabled={isDeleteLoading || !suspendReason}
                      >
                        Confirm Suspend{" "}
                        {isDeleteLoading && (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notifications" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Global Notifications</CardTitle>
              <CardDescription>
                Configure system-wide notification settings and alerts
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-base">Low Balance Alerts</Label>
                    <p className="text-sm text-gray-500">
                      Get notified when token distribution wallet balance is low
                    </p>
                  </div>
                  <Switch
                    checked={notifications.lowBalanceAlert}
                    onCheckedChange={(checked) =>
                      handleNotificationChange("lowBalanceAlert", checked)
                    }
                  />
                </div>

                {/* {notifications.lowBalanceAlert && (
                  <div className="ml-6 space-y-2">
                    <Label htmlFor="lowBalanceThreshold">Alert Threshold</Label>
                    <div className="flex items-center space-x-2">
                      <Input
                        id="lowBalanceThreshold"
                        type="number"
                        placeholder="1000"
                        value={notifications.lowBalanceThreshold}
                        onChange={(e) =>
                          handleNotificationChange(
                            "lowBalanceThreshold",
                            e.target.value
                          )
                        }
                        className="w-32"
                      />
                      <span className="text-sm text-gray-500">
                        tokens remaining
                      </span>
                    </div>
                  </div>
                )} */}

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-base">Campaign End Alerts</Label>
                    <p className="text-sm text-gray-500">
                      Get notified when campaigns are about to end or have ended
                    </p>
                  </div>
                  <Switch
                    checked={notifications.newCampaignAlert}
                    onCheckedChange={(checked) =>
                      handleNotificationChange("newCampaignAlert", checked)
                    }
                  />
                </div>

                {/* <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-base">
                      Suspicious Activity Alerts
                    </Label>
                    <p className="text-sm text-gray-500">
                      Get notified about potential fraud or suspicious user
                      behavior
                    </p>
                  </div>
                  <Switch
                    checked={notifications.suspiciousActivityAlert}
                    onCheckedChange={(checked) =>
                      handleNotificationChange(
                        "suspiciousActivityAlert",
                        checked
                      )
                    }
                  />
                </div> */}

                {/* <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-base">Email Notifications</Label>
                    <p className="text-sm text-gray-500">
                      Receive notifications via email in addition to in-app
                      alerts
                    </p>
                  </div>
                  <Switch
                    checked={notifications.emailNotifications}
                    onCheckedChange={(checked) =>
                      handleNotificationChange("emailNotifications", checked)
                    }
                  />
                </div> */}
              </div>

              <div className="flex justify-end pt-4">
                <Button
                  onClick={handleSaveNotifications}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save Notification Settings
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Current Status */}
          {/* <Card>
            <CardHeader>
              <CardTitle>System Status</CardTitle>
              <CardDescription>
                Current status of platform integrations and services
              </CardDescription>
            </CardHeader>

            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <div>
                      <p className="font-medium">Twitter API</p>
                      <p className="text-sm text-gray-500">
                        Connected and operational
                      </p>
                    </div>
                  </div>
                  <Badge className="bg-green-100 text-green-800">Online</Badge>
                </div>

                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center space-x-3">
                    <AlertTriangle className="h-5 w-5 text-yellow-600" />
                    <div>
                      <p className="font-medium">Telegram Bot</p>
                      <p className="text-sm text-gray-500">
                        Limited functionality - check token
                      </p>
                    </div>
                  </div>
                  <Badge className="bg-yellow-100 text-yellow-800">
                    Warning
                  </Badge>
                </div>

                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <div>
                      <p className="font-medium">Email Service</p>
                      <p className="text-sm text-gray-500">
                        All notifications are being delivered
                      </p>
                    </div>
                  </div>
                  <Badge className="bg-green-100 text-green-800">Online</Badge>
                </div>
              </div>
            </CardContent>
          </Card> */}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SystemSettings;
